import type { AbstractLeadFormProps, AbstractLeadFormState } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import { AbstractLeadForm } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import type { ProductType } from "@component/assets/AbstractLeadForm/types";
import { formatText } from "@shared/ui/format";
import type { EventComponentType } from "@shared/lib/google-analytics-4.type";
import { logError } from "@shared/lib/sentry-error";
import type { ReactNode } from "react";
import { calculate } from "./domain";
import * as Step from "./Steps";
import type {
	CreditInsuranceLeadCalcRequest,
	CreditInsuranceLeadCalcResponse,
	CreditInsuranceResponse,
	FormFields,
} from "./types";

interface State<TFormFields extends FormFields = FormFields> extends AbstractLeadFormState<TFormFields> {
	calculatorResponse: CreditInsuranceResponse;
}

/**
 * @Shortcode
 *
 * @title "Hitelfedezeti életbiztosítás kalkulátor"
 * @shortcode "credit_insurance_small"
 * @slug "biztositas"
 * @type "calculator"
 */
export class CreditInsuranceSmallCalculator extends AbstractLeadForm<
	CreditInsuranceLeadCalcRequest,
	CreditInsuranceLeadCalcResponse,
	FormFields,
	AbstractLeadFormProps<CreditInsuranceLeadCalcRequest, CreditInsuranceLeadCalcResponse>,
	State
> {
	// EventParams
	protected readonly productTypes: string = "LIFE_INSURANCE";
	protected readonly componentType: EventComponentType = "small-calculator";

	public static defaultProps = {
		title: "Kérj ingyenes segítséget szakértőnktől!",
		outsideTitle: "Hitelfedezeti Életbiztosítás",
		outsideTitleLink: "/biztositas/hitelfedezeti-eletbiztositas/",
		bottomLogo: true,
	};

	protected getInitialState(): State {
		return {
			...super.getInitialState(),
			activeStep: "step-1",
			calculatorResponse: undefined,
		};
	}

	protected renderSteps(): ReactNode {
		const {
			values: { email, durationYears },
			submitState,
			calculatorResponse,
		} = this.state;

		return (
			<>
				<Step.Step1 stepName="step-1" defaultTarget="result" />
				<Step.Result
					stepName="result"
					defaultTarget="lead-form"
					durationYears={durationYears}
					sumPrice={calculatorResponse?.sumPrice}
					canStepBack
				/>
				<Step.LeadForm
					stepName="lead-form"
					defaultTarget="success-page"
					button="Eredmény küldése"
					submitState={submitState}
					crossSellOption
					newsletterOption
				/>
				<Step.SuccessPage stepName="success-page" subscriptionTypes={["RETAIL_NEWSLETTER"]} userEmail={email} />
			</>
		);
	}

	protected onStep(target: string, withEvent: boolean): void {
		switch (target) {
			case "result":
				this.onResult(target);
				break;
			case "success-page":
				this.onSuccessPage();
				break;
			default:
				super.onStep(target, withEvent);
		}
	}

	private onResult(target: string): void {
		const { values } = this.state;

		this.setState({ submitState: "loading" });

		this.promisePool
			.add(calculate(values))
			.then((calculatorResponse: CreditInsuranceResponse) => {
				this.setState(
					{
						calculatorResponse,
						submitState: undefined,
					},
					() => {
						super.onStep(target, true);
						this.sendViewItemListEvent();
					},
				);

				this.sendCalculationEvent([]);
			})
			.catch((error: Error) => {
				this.setState({ submitState: "error" });

				this.promisePool.addTimeout().then(() => {
					this.setState({ submitState: undefined });
				});

				logError(error);
			});
	}

	private onSuccessPage(): void {
		this.setState({
			submitState: "loading",
		});
		this.sendLead()
			.then((_response) => {
				super.onStep("success-page", false);
			})
			.catch((error: Error) => {
				// TODO: Hibakezelés
				logError(error);
			});
	}

	private sendViewItemListEvent(): void {
		const {
			calculatorResponse,
			values: { durationYears },
		} = this.state;

		this.sendEvent("view_item_list", {
			item_list_id: this.calculationId,
			item_list_name: "normal",
			items: [
				{
					item_id: "",
					item_brand: "",
					item_name: "",
					affiliation: "product",
					index: 1,
					coupon: "none",
					currency: "HUF",
					discount: 0,
					item_category: "",
					item_category2: "",
					item_category3: "",
					item_category4: "",
					item_category5: "",
					item_variant: "",
					price: Math.floor(calculatorResponse?.sumPrice),
					quantity: durationYears,
				},
			],
		});
	}

	protected getLeadDataRequest(): CreditInsuranceLeadCalcRequest {
		const {
			values: { question, birthYear, durationYears },
		} = this.state;

		return {
			...super.getLeadDataRequest(),
			question,
			birthYear,
			preferredProduct: "Hitelfedezeti védelem",
			isCreditInsurance: true,
			insuranceDurationYears: durationYears,
		};
	}

	protected getCalculatorResponse(): CreditInsuranceLeadCalcResponse {
		const {
			values: { collateralAmount },
			calculatorResponse: { sumPrice },
		} = this.state;

		return {
			...super.getCalculatorResponse(),
			insuranceAmountCredit: Math.round(collateralAmount),
			insurancePriceCredit: Math.round(sumPrice),
		};
	}

	protected getAutoresponderName(): string {
		return "autoresponder-credit-insurance";
	}

	protected getAutoresponderFields(): Record<string, any> {
		const {
			values: { firstName, lastName, durationYears, collateralAmount },
			calculatorResponse: { sumPrice },
		} = this.state;

		return {
			lastName,
			firstName,

			durationYears,
			collateralAmount: formatText(collateralAmount, "ft"),
			sumPrice: formatText(sumPrice, "ft"),
		};
	}

	protected getProductType(): ProductType {
		return "LIFE_INSURANCE";
	}

	protected getComponentName(): string {
		return "CreditInsuranceSmallCalculator";
	}
}

export default CreditInsuranceSmallCalculator;
